import { useState, useEffect } from "react";
import { Box, CircularProgress, TextField, FormControlLabel, Checkbox } from "@mui/material";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import DataTable from "../../components/DataTable";

const Attendance = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const apiUrl = "https://oven-backend.onrender.com";
  // axios.get(`${apiUrl}/products`);
  
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${apiUrl}/employees/role/employee`);
        setEmployees(response.data);
      } catch (error) {
        console.error(t('error_fetching_employees'), error);
      }
    };

    fetchEmployees();
  }, [t]);

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const formattedDate = new Date(selectedDate).toISOString().split("T")[0];
        const response = await axios.get(`${apiUrl}/attendance?date=${formattedDate}`);
        
        const attendanceData = {};
        response.data.forEach(record => {
          attendanceData[record.employee_id._id] = {
            present: record.status === "present",
            attendanceId: record._id,
          };
        });
        setAttendance(attendanceData);
      } catch (error) {
        console.error(t('error_fetching_attendance'), error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [selectedDate]);

  const handleAttendanceChange = async (employeeId) => {
    const currentStatus = attendance[employeeId]?.present || false;
    const newStatus = !currentStatus;
    const attendanceId = attendance[employeeId]?.attendanceId;

    setAttendance((prev) => ({
      ...prev,
      [employeeId]: { present: newStatus, attendanceId },
    }));

    try {
      if (attendanceId) {
        await axios.patch(`${apiUrl}/attendance/${attendanceId}`, {
          employee_id: employeeId,
          date: selectedDate,
          status: newStatus ? "present" : "absent",
        });
      } else {
        const response = await axios.post(`${apiUrl}/attendance`, {
          employee_id: employeeId,
          date: selectedDate,
          status: newStatus ? "present" : "absent",
        });
        setAttendance((prev) => ({
          ...prev,
          [employeeId]: { present: newStatus, attendanceId: response.data._id },
        }));
      }
    } catch (error) {
      console.error(t('error_updating_attendance'), error);
    }
  };

  const columns = [
    { 
      field: "name", 
      headerName: t('employee_name'), 
      flex: 1,
      headerAlign: 'center',
      align: isRTL ? 'right' : 'left'
    },
    {
      field: "status",
      headerName: t('attendance'),
      flex: 1,
      renderCell: (params) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={attendance[params.row._id]?.present || false}
              onChange={() => handleAttendanceChange(params.row._id)}
              sx={{ 
                direction: isRTL ? 'rtl' : 'ltr',
                '& .MuiSvgIcon-root': { fontSize: 28 }
              }}
            />
          }
          label={attendance[params.row._id]?.present ? t('present') : t('absent')}
          sx={{ 
            marginLeft: 0,
            '& .MuiFormControlLabel-label': {
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Roboto, sans-serif'
            }
          }}
        />
      ),
      headerAlign: 'center',
      align: 'center'
    },
  ];

  return (
    <Box m="20px" sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <TextField
        label={t('select_date')}
        type="date"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        InputLabelProps={{ 
          shrink: true,
          sx: { 
            transformOrigin: isRTL ? 'right top' : 'left top',
            left: isRTL ? 'unset' : 0,
            right: isRTL ? 0 : 'unset'
          }
        }}
        sx={{ 
          mb: 3,
          '& .MuiInputBase-root': { direction: isRTL ? 'rtl' : 'ltr' }
        }}
        inputProps={{
          max: new Date().toISOString().split('T')[0],
          sx: { textAlign: isRTL ? 'right' : 'left' }
        }}
      />
      
      {loading ? (
        <CircularProgress />
      ) : (
        <DataTable 
          title={t('employee_attendance')}
          columns={columns}
          rows={employees}
          getRowId={(row) => row._id}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme => theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
              borderColor: theme => theme.palette.divider,
            },
            '& .MuiDataGrid-cell': {
              borderColor: theme => theme.palette.divider,
            },
          }}
        />
      )}
    </Box>
  );
};

export default Attendance;